import parse from 'html-react-parser';
import { useMemo } from 'react';
import TailwindTheme from 'tailwindcss/defaultTheme';

import { mapMaverickDemoStoreKey } from 'src/api/methods';
import type { MerchantFontSizeEnum, UseCase } from 'src/api/models';
export function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

export function assertString(val: unknown): asserts val is string {
  if (!val || typeof val !== 'string') {
    // oxlint-disable-next-line typescript/restrict-template-expressions
    throw new Error(`unexpected non string value: ${val}`);
  }
}

export function assertDefined<T>(val: undefined | null | T): asserts val is T {
  if (val === undefined || val === null) {
    throw new Error('unexpected undefined or null');
  }
}

export function fileExtension(filename: string): string {
  return filename
    .slice(((filename.lastIndexOf('.') - 1) >>> 0) + 2)
    .toLowerCase()
    .trim();
}

export function parseHtmlStringToReact(str: string) {
  return parse(str);
}

export function useParsedHtmlString(str: string | undefined): React.ReactNode {
  // Parsing is pure, so derive it during render rather than round-tripping through state in an
  // effect. That also drops the first-render `null`, which used to flash empty content in.
  return useMemo(() => (str ? parse(str) : null), [str]);
}

export function isUseCaseAbandonedCart(useCase: UseCase): boolean {
  return useCase.name.includes('abandoned');
}

export function removeNulls<T>(obj: T | null | undefined): obj is T {
  return obj !== null || obj !== undefined;
}

export function mapFontSizes(merchangeFontSize: MerchantFontSizeEnum): `text-${keyof typeof TailwindTheme.fontSize}` {
  type FontSizeRawToTwnd = Record<MerchantFontSizeEnum, `text-${keyof typeof TailwindTheme.fontSize}`>;
  const fontSizeRawToTwnd: FontSizeRawToTwnd = {
    XS: 'text-xs',
    SM: 'text-sm',
    BASE: 'text-base',
    LG: 'text-lg',
    XL: 'text-xl',
    TWO_XL: 'text-2xl',
    THREE_XL: 'text-3xl',
    FOUR_XL: 'text-4xl',
  };
  return fontSizeRawToTwnd[merchangeFontSize];
}

function isMaverickDemoStore(storeKeyName: string | undefined): boolean {
  return !!storeKeyName && storeKeyName in mapMaverickDemoStoreKey;
}

// A demo runs on another merchant's real audit log, so anything identifying one of their shoppers
// is masked on screen. Display only — the responses still carry the real values.
export function maskDemoEmail(email: string, storeKeyName: string | undefined): string {
  if (!isMaverickDemoStore(storeKeyName)) {
    return email;
  }
  const at = email.indexOf('@');
  if (at < 1) {
    return '****';
  }
  return `${email[0]}****${email.slice(at)}`;
}

// The sender address on a campaign variant is the merchant's own, so its domain names them as
// plainly as the brand does — unlike a shopper's address, where the domain is just a mailbox host.
export function maskDemoSender(email: string, storeKeyName: string | undefined): string {
  if (!isMaverickDemoStore(storeKeyName)) {
    return email;
  }
  const dot = email.lastIndexOf('.');
  const tld = dot > email.indexOf('@') ? email.slice(dot) : '';
  return `${email[0] ?? ''}****@****${tld}`;
}

export function maskDemoValue(value: string | null, storeKeyName: string | undefined, keep = 1): string | null {
  if (value === null || !isMaverickDemoStore(storeKeyName)) {
    return value;
  }
  return `${value.slice(0, keep)}****`;
}

export function maskDemoIp(ip: string | null, storeKeyName: string | undefined): string | null {
  if (ip === null || !isMaverickDemoStore(storeKeyName)) {
    return ip;
  }
  // Shopify sends IPv6 for shoppers on an IPv6-only network, and a dotted-quad assumption would
  // leave those addresses whole; anything that isn't four octets is masked outright.
  const octets = ip.split('.');
  return octets.length === 4 ? `${octets[0]}.***.***.***` : '****';
}

// Merchant content — campaign, flow and message names — identifies the backing store as readily
// as a shopper's address does: Klaviyo names routinely carry the brand, a product or a send date.
// Keep enough of the head for the row to stay distinguishable, drop the rest.
export function maskDemoName(name: string | null, storeKeyName: string | undefined): string | null {
  if (name === null || !isMaverickDemoStore(storeKeyName)) {
    return name;
  }
  const [first = ''] = name.trim().split(/\s+/);
  const head = first.slice(0, 10);
  return head ? `${head}***` : '***';
}

// A response identifier is an email address for email replies and a phone number for SMS.
export function maskDemoContact(identifier: string, storeKeyName: string | undefined): string {
  if (!isMaverickDemoStore(storeKeyName)) {
    return identifier;
  }
  if (identifier.includes('@')) {
    return maskDemoEmail(identifier, storeKeyName);
  }
  return `${identifier.slice(0, 2)}****`;
}

export function replaceDemoStoreName(text: string, storeKeyName: string | undefined): string {
  const isMaverickDemo = storeKeyName && storeKeyName in mapMaverickDemoStoreKey;
  if (!isMaverickDemo) {
    return text;
  }
  return text
    .replaceAll('Sassy Saints', 'Maverick')
    .replaceAll('Sassy', 'Maverick')
    .replaceAll('SASSY', 'MAVERICK')
    .replaceAll('Cakesbody', 'Maverick')
    .replaceAll('Cakes Body', 'Maverick')
    .replaceAll('CAKESBODY', 'MAVERICK')
    .replaceAll('CAKES BODY', 'MAVERICK')
    .replaceAll('Magic Mind', 'Maverick')
    .replaceAll('MAGIC MIND', 'MAVERICK')
    .replaceAll('MAGICMIND', 'MAVERICK')
    .replaceAll('Manly Bands', 'Maverick')
    .replaceAll('ManlyBands', 'Maverick')
    .replaceAll('MANLY BANDS', 'MAVERICK')
    .replaceAll('MANLYBANDS', 'MAVERICK');
}

export async function sha256Hash(message: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(message); // Encode the message as UTF-8
  const hashBuffer = await crypto.subtle.digest('SHA-256', data); // Calculate the hash
  const hashArray = Array.from(new Uint8Array(hashBuffer)); // Convert ArrayBuffer to Array of bytes
  const hexHash = hashArray.map((b) => b.toString(16).padStart(2, '0')).join(''); // Convert bytes to hex string
  return hexHash;
}
